import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { client } from '../api/client';
import { ChatModal } from '../components/property/ChatModal';
import { useToast } from '../components/common/Toast';
import { NotificationPreferences } from '../components/common/NotificationPreferences';

type NotificationTab = 'sve' | 'poklapanja' | 'poruke' | 'licitacije' | 'termini' | 'sistem';

interface Appointment {
  id: number;
  propertyId: number;
  propertyTitle?: string;
  requesterId: string;
  requesterName?: string;
  ownerId: string;
  scheduledDate: number;
  scheduledTime: string;
  message?: string;
  status: string;
  phone?: string;
  createdAt: number;
}

interface Message {
  id: number;
  senderId: string;
  senderName?: string;
  senderEmail?: string;
  isAdminMessage?: boolean;
  receiverId: string;
  receiverName?: string;
  otherUserId?: string;
  otherUserName?: string;
  isSent?: boolean;
  propertyId: number;
  propertyTitle?: string;
  subject?: string;
  content: string;
  isRead: number;
  createdAt: number;
}

interface Conversation {
  id: string; // unique key: `${senderId}-${propertyId}`
  otherUserId: string;
  otherUserName: string;
  propertyId: number | null;
  propertyTitle: string | null;
  lastMessage: string;
  lastMessageDate: number;
  unreadCount: number;
  isAdminMessage: boolean;
}

interface PropertyMatch {
  id: number;
  demandId: number;
  offerId: number;
  matchScore: number;
  status: string;
  createdAt: number;
  demand?: { id: number; title: string };
  offer?: { id: number; title: string; price: number };
}

interface BackendNotification {
  id: number;
  userId: string;
  type: string;
  notificationType?: string;
  title: string;
  message: string;
  link?: string;
  isRead: number;
  propertyId?: number;
  senderId?: string;
  createdAt: number;
}

interface UserBid {
  id: number;
  propertyId: number;
  propertyTitle: string;
  propertyImage: string | null;
  propertyPrice: number;
  currentBid: number;
  auctionEndDate: string | null;
  propertyStatus: string;
  ownerName: string;
  amount: number;
  comment: string | null;
  status: 'pending' | 'approved' | 'rejected';
  ownerResponse: string | null;
  respondedAt: string | null;
  createdAt: string;
}

interface ReceivedBid {
  id: number;
  propertyId: number;
  propertyTitle: string;
  propertyImage: string | null;
  bidderName: string;
  bidderEmail: string;
  bidderPhone: string;
  bidderId: number;
  amount: number;
  comment: string | null;
  status: 'pending' | 'approved' | 'rejected';
  ownerResponse: string | null;
  respondedAt: string | null;
  createdAt: string;
}

import { Pagination } from '../components/common/Pagination';

export function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isChecking, user, notifications, markNotificationRead, setNotifications } = useAppStore();
  const { language } = useI18n();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<NotificationTab>('sve');
  const [loading, setLoading] = useState(true);
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [matches, setMatches] = useState<PropertyMatch[]>([]);
  const [backendNotifications, setBackendNotifications] = useState<BackendNotification[]>([]);
  const [myBids, setMyBids] = useState<UserBid[]>([]);
  const [receivedBids, setReceivedBids] = useState<ReceivedBid[]>([]);
  const [bidsSubTab, setBidsSubTab] = useState<'my' | 'received'>('my');
  const [respondingToBid, setRespondingToBid] = useState<ReceivedBid | null>(null);
  const [bidResponse, setBidResponse] = useState('');

  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [appointmentResponse, setAppointmentResponse] = useState<{id: number, status: string, message: string} | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<{senderId: string, senderName: string, propertyId: number | null, propertyTitle: string | null} | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingThread, setLoadingThread] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messageOffset, setMessageOffset] = useState(0);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const MESSAGES_PER_PAGE = 20;

  // Group messages into conversations
  const conversations = useMemo((): Conversation[] => {
    const convMap = new Map<string, Conversation>();
    
    messages.forEach(msg => {
      // Use otherUserId from backend (handles both sent and received)
      const otherUserId = msg.otherUserId || msg.senderId;
      const otherUserName = msg.otherUserName || msg.senderName || 'Korisnik';
      
      // Key by other user + property combination
      const key = `${otherUserId}-${msg.propertyId || 0}`;
      const existing = convMap.get(key);
      
      // Only count unread for received messages (not sent)
      const isUnread = !msg.isSent && !msg.isRead;
      
      if (!existing || msg.createdAt > existing.lastMessageDate) {
        convMap.set(key, {
          id: key,
          otherUserId: otherUserId,
          otherUserName: otherUserName,
          propertyId: msg.propertyId || null,
          propertyTitle: msg.propertyTitle || null,
          lastMessage: msg.content,
          lastMessageDate: msg.createdAt,
          unreadCount: existing ? existing.unreadCount + (isUnread ? 1 : 0) : (isUnread ? 1 : 0),
          isAdminMessage: msg.isAdminMessage || false
        });
      } else if (existing && isUnread) {
        existing.unreadCount++;
      }
    });
    
    return Array.from(convMap.values()).sort((a, b) => b.lastMessageDate - a.lastMessageDate);
  }, [messages]);

  // Refresh only messages (not full page)
  const refreshMessagesOnly = useCallback(async () => {
    try {
      const msgRes = await client.api.fetch('/api/messages/inbox');
      if (msgRes.ok) {
        const msgData = await msgRes.json();
        setMessages(msgData.data || []);
      }
    } catch (err) {
      console.error('Error refreshing messages:', err);
    }
  }, []);

  // Scroll to bottom of messages (only within chat container)
  const scrollToBottom = useCallback((smooth = true) => {
    setTimeout(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  }, []);

  // Load conversation thread
  const loadConversationThread = useCallback(async (conv: Conversation) => {
    setLoadingThread(true);
    setMessageOffset(0);
    setHasMoreMessages(true);
    setSelectedConversation({
      senderId: conv.otherUserId,
      senderName: conv.otherUserName,
      propertyId: conv.propertyId,
      propertyTitle: conv.propertyTitle
    });
    
    try {
      const res = await client.api.fetch(`/api/messages/thread/${conv.propertyId || 0}/${conv.otherUserId}?limit=${MESSAGES_PER_PAGE}`);
      if (res.ok) {
        const data = await res.json();
        const msgs = data.data || [];
        setConversationMessages(msgs);
        setHasMoreMessages(msgs.length >= MESSAGES_PER_PAGE);
        // Scroll to bottom after loading
        scrollToBottom(false);
        // Refresh only messages to update unread counts (without resetting UI)
        refreshMessagesOnly();
      }
    } catch (err) {
      console.error('Error loading thread:', err);
    } finally {
      setLoadingThread(false);
    }
  }, [refreshMessagesOnly, scrollToBottom]);

  // Load older messages when scrolling up
  const loadOlderMessages = useCallback(async () => {
    if (!selectedConversation || loadingOlderMessages || !hasMoreMessages) return;
    
    setLoadingOlderMessages(true);
    const container = messagesContainerRef.current;
    const previousScrollHeight = container?.scrollHeight || 0;
    
    try {
      const newOffset = messageOffset + MESSAGES_PER_PAGE;
      const res = await client.api.fetch(
        `/api/messages/thread/${selectedConversation.propertyId || 0}/${selectedConversation.senderId}?limit=${MESSAGES_PER_PAGE}&offset=${newOffset}`
      );
      if (res.ok) {
        const data = await res.json();
        const olderMsgs = data.data || [];
        if (olderMsgs.length > 0) {
          // Prepend older messages
          setConversationMessages(prev => [...olderMsgs, ...prev]);
          setMessageOffset(newOffset);
          setHasMoreMessages(olderMsgs.length >= MESSAGES_PER_PAGE);
          // Maintain scroll position
          setTimeout(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - previousScrollHeight;
            }
          }, 0);
        } else {
          setHasMoreMessages(false);
        }
      }
    } catch (err) {
      console.error('Error loading older messages:', err);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [selectedConversation, loadingOlderMessages, hasMoreMessages, messageOffset]);

  // Handle scroll to detect when user scrolls to top
  const handleMessagesScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    if (scrollTop === 0 && hasMoreMessages && !loadingOlderMessages) {
      loadOlderMessages();
    }
  }, [hasMoreMessages, loadingOlderMessages, loadOlderMessages]);

  // Send message in conversation
  const handleSendInConversation = async () => {
    if (!selectedConversation || !newMessage.trim()) return;
    
    const messageToSend = newMessage.trim();
    setNewMessage(''); // Clear immediately for better UX
    
    // Optimistically add message to conversation
    const optimisticMessage = {
      id: Date.now(), // Temporary ID
      senderId: user?.id || '',
      senderName: user?.name || 'Vi',
      content: messageToSend,
      createdAt: Math.floor(Date.now() / 1000),
      isMe: true
    };
    setConversationMessages(prev => [...prev, optimisticMessage]);
    scrollToBottom(); // Scroll to new message
    
    try {
      const res = await client.api.fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          receiverId: selectedConversation.senderId,
          propertyId: selectedConversation.propertyId,
          message: messageToSend
        })
      });
      
      if (res.ok) {
        // Refresh only messages inbox in background (without resetting UI)
        refreshMessagesOnly();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setConversationMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setNewMessage(messageToSend); // Restore message
    }
  };

  // Polling for new messages (every 10 seconds when on Poruke tab)
  useEffect(() => {
    if (activeTab !== 'poruke' || !isAuthenticated) return;
    
    const pollInterval = setInterval(async () => {
      await refreshMessagesOnly();
      
      // Also refresh current thread if open
      if (selectedConversation) {
        try {
          const res = await client.api.fetch(`/api/messages/thread/${selectedConversation.propertyId || 0}/${selectedConversation.senderId}`);
          if (res.ok) {
            const data = await res.json();
            setConversationMessages(data.data || []);
          }
        } catch (err) {
          // Silent fail for polling
        }
      }
    }, 1000); // Poll every 1 second for real-time feel
    
    return () => clearInterval(pollInterval);
  }, [activeTab, isAuthenticated, selectedConversation, refreshMessagesOnly]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['sve', 'poklapanja', 'poruke', 'licitacije', 'termini', 'sistem'].includes(tab)) {
      setActiveTab(tab as NotificationTab);
    }
    
    const chatId = searchParams.get('chat');
    if (chatId) {
      setSelectedChatId(chatId);
    }
    
    // Handle opening conversation from notification link
    const senderId = searchParams.get('sender');
    const propertyId = searchParams.get('property');
    if (senderId && messages.length > 0) {
      // Find the conversation matching sender and property
      const propId = propertyId ? parseInt(propertyId) : 0;
      const matchingConv = conversations.find(
        c => c.otherUserId === senderId && (c.propertyId || 0) === propId
      );
      if (matchingConv) {
        loadConversationThread(matchingConv);
        // Clear the params after opening
        setSearchParams({ tab: 'poruke' });
      }
    }
  }, [searchParams, messages.length, conversations]);

  // Fetch data from backend
  const fetchData = useCallback(async () => {
    if (!isAuthenticated || isChecking) return;
    
    setLoading(true);
    try {
      // Fetch notifications from backend
      const notifRes = await client.api.fetch('/api/notifications');
      if (notifRes.ok) {
        const notifData = await notifRes.json();
        setBackendNotifications(notifData.data || []);
      }

      // Fetch messages
      const msgRes = await client.api.fetch('/api/messages/inbox');
      if (msgRes.ok) {
        const msgData = await msgRes.json();
        setMessages(msgData.data || []);
      }

      // Fetch appointments (incoming)
      const aptRes = await client.api.fetch('/api/scheduling/incoming');
      if (aptRes.ok) {
        const aptData = await aptRes.json();
        setAppointments(aptData.data || []);
      }

      // Fetch matches
      const matchRes = await client.api.fetch('/api/matches');
      if (matchRes.ok) {
        const matchData = await matchRes.json();
        setMatches(matchData.data || []);
      }

      // Fetch my bids (auctions I'm bidding on)
      const myBidsRes = await client.api.fetch('/api/my-bids');
      if (myBidsRes.ok) {
        const myBidsData = await myBidsRes.json();
        setMyBids(myBidsData.data || []);
      }

      // Fetch received bids (offers on my properties)
      const receivedBidsRes = await client.api.fetch('/api/received-bids');
      if (receivedBidsRes.ok) {
        const receivedBidsData = await receivedBidsRes.json();
        setReceivedBids(receivedBidsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isChecking, setNotifications]);

  useEffect(() => {
    if (isChecking) return; // Wait for auth check
    if (!isAuthenticated) {
      navigate('/prijava');
      return;
    }
    fetchData();
  }, [isAuthenticated, isChecking, navigate, fetchData]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  // Notification type helpers for filtering
  const isMessageNotification = (n: BackendNotification) => 
    n.type === 'message' || n.notificationType === 'new_message';
  
  const isAppointmentNotification = (n: BackendNotification) => 
    n.type === 'appointment' || ['appointment_request', 'appointment_confirmed', 'appointment_cancelled'].includes(n.notificationType || '');
  
  const isSystemNotification = (n: BackendNotification) =>
    ['admin', 'system', 'expiring', 'listing_approved', 'listing_rejected', 'token'].includes(n.type) ||
    ['listing_approved', 'listing_rejected', 'listing_expiring', 'system'].includes(n.notificationType || '');

  // Count unread message-type notifications
  const unreadMessageNotifs = backendNotifications.filter(n => !n.isRead && isMessageNotification(n)).length;
  const unreadAppointmentNotifs = backendNotifications.filter(n => !n.isRead && isAppointmentNotification(n)).length;

  const tabs: { id: NotificationTab; label: string; icon: JSX.Element; count?: number }[] = [
    { 
      id: 'sve', 
      label: language === 'sr' ? 'Sve' : 'All',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
      count: backendNotifications.filter(n => !n.isRead).length
    },
    { 
      id: 'poklapanja', 
      label: language === 'sr' ? 'Poklapanja' : 'Matches',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>,
      count: matches.filter(m => m.status !== 'dismissed').length
    },
    { 
      id: 'poruke', 
      label: language === 'sr' ? 'Poruke' : 'Messages',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>,
      count: messages.filter(m => !m.isSent && !m.isRead).length + unreadMessageNotifs
    },
    { 
      id: 'licitacije', 
      label: language === 'sr' ? 'Licitacije' : 'Bids',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
      count: receivedBids.filter(b => b.status === 'pending').length + myBids.filter(b => b.status === 'pending').length
    },
    { 
      id: 'termini', 
      label: language === 'sr' ? 'Termini' : 'Appointments',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
      count: appointments.filter(a => a.status === 'pending').length + unreadAppointmentNotifs
    },
    { 
      id: 'sistem', 
      label: language === 'sr' ? 'Podešavanja' : 'Settings',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
    },
  ];

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return language === 'sr' ? 'upravo sada' : 'just now';
    if (mins < 60) return `${mins} min`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('sr-RS');
  };

  const formatDateTime = (timestamp: number, time?: string) => {
    const date = new Date(timestamp * 1000);
    const dateStr = date.toLocaleDateString('sr-RS', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
    });
    return time ? `${dateStr} ${time}` : dateStr;
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await client.api.fetch('/api/notifications/read-all', { method: 'PUT' });
      if (res.ok) {
        setBackendNotifications(prev => prev.map(n => ({ ...n, isRead: 1 })));
        notifications.forEach(n => {
          if (!n.read) markNotificationRead(n.id);
        });
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleMarkNotificationRead = async (notifId: number) => {
    try {
      await client.api.fetch(`/api/notifications/${notifId}/read`, { method: 'PUT' });
      setBackendNotifications(prev => prev.map(n =>
        n.id === notifId ? { ...n, isRead: 1 } : n
      ));
      markNotificationRead(String(notifId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (notifId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent click handler
    try {
      const res = await client.api.fetch(`/api/notifications/${notifId}`, { method: 'DELETE' });
      if (res.ok) {
        setBackendNotifications(prev => prev.filter(n => n.id !== notifId));
        // Also remove from store notifications
        setNotifications(notifications.filter(n => n.id !== String(notifId)));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleAppointmentResponse = async (appointmentId: number, status: 'confirmed' | 'rejected') => {
    try {
      const res = await client.api.fetch(`/scheduling/${appointmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        setAppointments(prev => prev.map(a => 
          a.id === appointmentId ? { ...a, status } : a
        ));
      }
    } catch (error) {
      console.error('Error responding to appointment:', error);
    }
  };

  const handleDeleteAppointment = async (appointmentId: number) => {
    toast.showConfirm({
      title: language === 'sr' ? 'Obriši termin?' : 'Delete appointment?',
      message: language === 'sr' ? 'Ova akcija je nepovratna.' : 'This action cannot be undone.',
      type: 'danger',
      confirmText: language === 'sr' ? 'Obriši' : 'Delete',
      cancelText: language === 'sr' ? 'Otkaži' : 'Cancel',
      onConfirm: async () => {
        try {
          const res = await client.api.fetch(`/scheduling/${appointmentId}`, {
            method: 'DELETE',
          });
          if (res.ok) {
            toast.success(
              language === 'sr' ? 'Termin obrisan' : 'Appointment deleted',
              language === 'sr' ? 'Termin je uspešno obrisan.' : 'Appointment has been deleted.'
            );
            setAppointments(prev => prev.filter(a => a.id !== appointmentId));
          } else {
            toast.error(language === 'sr' ? 'Greška' : 'Error', language === 'sr' ? 'Greška pri brisanju termina' : 'Error deleting appointment');
          }
        } catch (error) {
          console.error('Error deleting appointment:', error);
          toast.error(language === 'sr' ? 'Greška' : 'Error', language === 'sr' ? 'Greška pri brisanju termina' : 'Error deleting appointment');
        }
      }
    });
  };

  const handleSendReply = async () => {
    if (!replyTo || !replyContent.trim()) return;
    
    try {
      const res = await client.api.fetch('/api/messages/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalMessageId: replyTo.id,
          content: replyContent,
        }),
      });
      
      if (res.ok) {
        toast.success(language === 'sr' ? 'Poruka poslata' : 'Message sent', language === 'sr' ? 'Poruka je uspešno poslata!' : 'Message sent successfully!');
        setReplyTo(null);
        setReplyContent('');
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(language === 'sr' ? 'Greška' : 'Error', language === 'sr' ? 'Greška pri slanju poruke' : 'Error sending message');
    }
  };

  const handleDismissMatch = async (matchId: number) => {
    try {
      await client.api.fetch(`/api/matches/${matchId}/dismiss`, { method: 'PUT' });
      setMatches(prev => prev.filter(m => m.id !== matchId));
    } catch (error) {
      console.error('Error dismissing match:', error);
    }
  };

  const handleBidResponse = async (approve: boolean) => {
    if (!respondingToBid) return;
    
    try {
      const endpoint = approve 
        ? `/api/bids/${respondingToBid.id}/approve`
        : `/api/bids/${respondingToBid.id}/reject`;
      
      const res = await client.api.fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: bidResponse }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(
          language === 'sr' ? (approve ? 'Ponuda prihvaćena' : 'Ponuda odbijena') : (approve ? 'Bid accepted' : 'Bid rejected'),
          language === 'sr' ? (approve ? 'Ponuda je uspešno prihvaćena!' : 'Ponuda je odbijena.') : (approve ? 'Bid accepted successfully!' : 'Bid has been rejected.')
        );
        setRespondingToBid(null);
        setBidResponse('');
        // Update local state
        setReceivedBids(prev => prev.map(b => 
          b.id === respondingToBid.id 
            ? { ...b, status: approve ? 'approved' : 'rejected', ownerResponse: bidResponse, respondedAt: new Date().toISOString() }
            : b
        ));
      } else {
        toast.error(language === 'sr' ? 'Greška' : 'Error', data.error || (language === 'sr' ? 'Greška pri obradi ponude' : 'Error processing bid'));
      }
    } catch (error) {
      console.error('Error responding to bid:', error);
      toast.error(language === 'sr' ? 'Greška' : 'Error', language === 'sr' ? 'Greška pri obradi ponude' : 'Error processing bid');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'match':
        return <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">🔗</div>;
      case 'bid':
        return <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">🔨</div>;
      case 'expiring':
        return <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">⏰</div>;
      case 'admin':
        return <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">👑</div>;
      case 'message':
        return <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">💬</div>;
      case 'appointment':
        return <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">📅</div>;
      default:
        return <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-400">🔔</div>;
    }
  };

  const filteredNotifications = (() => {
    // Use only backend notifications (store notifications are already synced from backend)
    const allNotifs = [...backendNotifications].sort((a, b) => b.createdAt - a.createdAt);

    if (activeTab === 'sve') return allNotifs;
    if (activeTab === 'sistem') return allNotifs.filter(n => isSystemNotification(n));
    return [];
  })();

  // Get appointment-related notifications for termini tab
  const appointmentNotifications = backendNotifications.filter(n => isAppointmentNotification(n)).sort((a, b) => b.createdAt - a.createdAt);
  
  // Get message-related notifications for poruke tab (to show alongside actual messages)
  const messageNotifications = backendNotifications.filter(n => isMessageNotification(n)).sort((a, b) => b.createdAt - a.createdAt);

  // Calculate pagination
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    if (activeTab === 'sve') {
      return {
        data: filteredNotifications.slice(startIndex, endIndex),
        total: filteredNotifications.length
      };
    }
    if (activeTab === 'sistem') {
      // Sistem tab now shows email settings, no pagination needed
      return { data: [], total: 0 };
    }
    if (activeTab === 'poklapanja') {
      // Show all matches (new, viewed, contacted) - exclude only 'dismissed'
      const filtered = matches.filter(m => m.status !== 'dismissed');
      return {
        data: filtered.slice(startIndex, endIndex),
        total: filtered.length
      };
    }
    if (activeTab === 'poruke') {
      // Show all messages or just unread? The tab count shows unread.
      // The list usually shows all. Let's assume all for now, or check existing code.
      // Existing code: messages.filter(m => !m.isRead).length for count.
      // But render loop?
      // I need to check the render loop for messages.
      return {
        data: messages.slice(startIndex, endIndex),
        total: messages.length
      };
    }
    if (activeTab === 'termini') {
      return {
        data: appointments.slice(startIndex, endIndex),
        total: appointments.length
      };
    }
    if (activeTab === 'licitacije') {
      const allBids = bidsSubTab === 'my' ? myBids : receivedBids;
      return {
        data: allBids.slice(startIndex, endIndex),
        total: allBids.length
      };
    }
    return { data: [], total: 0 };
  };

  const { data: paginatedData, total: totalItems } = getPaginatedData();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {language === 'sr' ? 'Notifikacije' : 'Notifications'}
          </h1>
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-amber-600 hover:text-amber-700 font-medium"
          >
            {language === 'sr' ? 'Označi sve kao pročitano' : 'Mark all as read'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-amber-600">{backendNotifications.filter(n => !n.isRead).length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Nepročitano' : 'Unread'}</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-pink-600">{matches.filter(m => m.status !== 'dismissed').length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Poklapanja' : 'Matches'}</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{messages.filter(m => !m.isSent && !m.isRead).length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Poruke' : 'Messages'}</div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-emerald-600">{appointments.filter(a => a.status === 'pending').length + receivedBids.filter(b => b.status === 'pending').length}</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">{language === 'sr' ? 'Na čekanju' : 'Pending'}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm mb-6">
          <div className="flex flex-wrap border-b border-slate-200 dark:border-slate-700">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 sm:px-5 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-amber-600 border-b-2 border-amber-500 -mb-px'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {tab.icon}
                <span className="hidden md:inline">{tab.label}</span>
                {tab.count && tab.count > 0 && (
                  <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {/* Sistem tab - Email notification settings */}
            {activeTab === 'sistem' && (
              <div className="space-y-4">
                <NotificationPreferences defaultExpanded={true} />
              </div>
            )}

            {/* Sve notifikacije */}
            {activeTab === 'sve' && (
              <div className="space-y-3">
                {paginatedData.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {language === 'sr' ? 'Nema notifikacija' : 'No notifications'}
                  </div>
                ) : (
                  (paginatedData as BackendNotification[]).map(notif => (
                    <motion.div
                      key={notif.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex items-start gap-4 p-4 rounded-lg transition-colors cursor-pointer ${
                        notif.isRead
                          ? 'bg-slate-50 dark:bg-slate-700/50'
                          : 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500'
                      }`}
                      onClick={() => {
                        if (!notif.isRead) handleMarkNotificationRead(notif.id);
                        if (notif.link) navigate(notif.link);
                      }}
                    >
                      {getNotificationIcon(notif.type)}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 dark:text-white">{notif.title}</h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{notif.message}</p>
                        <span className="text-xs text-slate-500 mt-2 block">{formatDate(notif.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notif.isRead && (
                          <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                        )}
                        <button
                          onClick={(e) => handleDeleteNotification(notif.id, e)}
                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title={language === 'sr' ? 'Obriši notifikaciju' : 'Delete notification'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Poklapanja */}
            {activeTab === 'poklapanja' && (
              <div className="space-y-4">
                {paginatedData.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {language === 'sr' ? 'Nema poklapanja' : 'No matches yet'}
                    <p className="text-sm mt-2">{language === 'sr' ? 'Kada se vaša potražnja poklopi sa ponudom, videćete ovde.' : 'When your demand matches an offer, you\'ll see it here.'}</p>
                  </div>
                ) : (
                  (paginatedData as PropertyMatch[]).map(match => {
                    // Determine which listing belongs to current user
                    // isMyDemand: true means user owns the demand (Potražnja)
                    // isMyDemand: false means user owns the offer (Ponuda)
                    const isMyDemand = (match as any).isMyDemand;
                    
                    return (
                    <motion.div
                      key={match.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-gradient-to-r from-blue-50 to-emerald-50 dark:from-blue-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-blue-600 font-medium flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          {language === 'sr' ? 'Poklapanje' : 'Match'} {match.matchScore}%
                        </span>
                        <span className="text-xs text-slate-500">{formatDate(match.createdAt)}</span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Left side: User's listing */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                          <span className={`text-xs font-medium ${isMyDemand ? 'text-blue-600' : 'text-emerald-600'} mb-1 block`}>
                            {isMyDemand 
                              ? (language === 'sr' ? 'VAŠA POTRAŽNJA' : 'YOUR DEMAND')
                              : (language === 'sr' ? 'VAŠA PONUDA' : 'YOUR OFFER')
                            }
                          </span>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {isMyDemand ? (match.demand?.title || `Potražnja #${match.demandId}`) : (match.offer?.title || `Ponuda #${match.offerId}`)}
                          </p>
                          <Link to={`/oglas/${isMyDemand ? match.demandId : match.offerId}`} className="text-sm text-amber-600 hover:underline mt-2 inline-block">
                            {language === 'sr' ? 'Pogledaj →' : 'View →'}
                          </Link>
                        </div>
                        {/* Right side: Matched listing */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                          <span className={`text-xs font-medium ${isMyDemand ? 'text-emerald-600' : 'text-blue-600'} mb-1 block`}>
                            {isMyDemand 
                              ? (language === 'sr' ? 'ODGOVARAJUĆA PONUDA' : 'MATCHING OFFER')
                              : (language === 'sr' ? 'ODGOVARAJUĆA POTRAŽNJA' : 'MATCHING DEMAND')
                            }
                          </span>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {isMyDemand ? (match.offer?.title || `Ponuda #${match.offerId}`) : (match.demand?.title || `Potražnja #${match.demandId}`)}
                          </p>
                          {isMyDemand && match.offer?.price && <p className="text-sm text-slate-600 dark:text-slate-400">{match.offer.price.toLocaleString()} EUR</p>}
                          {!isMyDemand && match.demand?.price && <p className="text-sm text-slate-600 dark:text-slate-400">{match.demand.price.toLocaleString()} EUR</p>}
                          <Link to={`/oglas/${isMyDemand ? match.offerId : match.demandId}`} className="text-sm text-amber-600 hover:underline mt-2 inline-block">
                            {language === 'sr' ? 'Pogledaj →' : 'View →'}
                          </Link>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button 
                          onClick={() => window.open(`${window.location.origin}/pages/kontakt-oglas.php?id=${isMyDemand ? match.offerId : match.demandId}`, '_blank')}
                          className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
                        >
                          {language === 'sr' ? 'Kontaktiraj vlasnika' : 'Contact Owner'}
                        </button>
                        <button 
                          onClick={() => handleDismissMatch(match.id)}
                          className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          {language === 'sr' ? 'Odbaci' : 'Dismiss'}
                        </button>
                      </div>
                    </motion.div>
                  );
                  })
                )}
              </div>
            )}

            {/* Poruke */}
            {activeTab === 'poruke' && (
              <div className="flex flex-col md:flex-row gap-4 min-h-[400px]">
                {/* Conversations List */}
                <div className={`${selectedConversation ? 'hidden md:block' : ''} md:w-1/3 space-y-2`}>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                    {language === 'sr' ? 'Konverzacije' : 'Conversations'}
                  </h3>
                  
                  {conversations.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      {language === 'sr' ? 'Nema poruka' : 'No messages'}
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <motion.div
                        key={conv.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        onClick={() => loadConversationThread(conv)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedConversation?.senderId === conv.otherUserId && selectedConversation?.propertyId === conv.propertyId
                            ? 'bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-500'
                            : conv.unreadCount > 0
                            ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                            : 'bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 ${
                            conv.isAdminMessage 
                              ? 'bg-gradient-to-br from-amber-500 to-amber-700' 
                              : 'bg-gradient-to-br from-slate-400 to-slate-600'
                          }`}>
                            {conv.isAdminMessage ? '👑' : (conv.otherUserName?.charAt(0) || 'K')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-slate-900 dark:text-white truncate">
                                {conv.otherUserName}
                                {conv.isAdminMessage && (
                                  <span className="ml-1 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
                                    Admin
                                  </span>
                                )}
                              </span>
                              {conv.unreadCount > 0 && (
                                <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                  {conv.unreadCount}
                                </span>
                              )}
                            </div>
                            {conv.propertyTitle && (
                              <p className="text-xs text-amber-600 truncate">{conv.propertyTitle}</p>
                            )}
                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{conv.lastMessage}</p>
                            <span className="text-xs text-slate-400">{formatDate(conv.lastMessageDate)}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>

                {/* Chat View */}
                <div className={`${!selectedConversation ? 'hidden md:flex' : 'flex'} flex-1 flex-col bg-slate-100 dark:bg-slate-700/30 rounded-lg overflow-hidden`}>
                  {selectedConversation ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
                        <button
                          onClick={() => setSelectedConversation(null)}
                          className="md:hidden p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white font-medium">
                          {selectedConversation.senderName?.charAt(0) || 'K'}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">{selectedConversation.senderName}</h4>
                          {selectedConversation.propertyTitle && (
                            <Link to={`/oglas/${selectedConversation.propertyId}`} className="text-xs text-amber-600 hover:underline">
                              {selectedConversation.propertyTitle}
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Messages */}
                      <div 
                        ref={messagesContainerRef}
                        onScroll={handleMessagesScroll}
                        className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]"
                      >
                        {/* Loading older messages indicator */}
                        {loadingOlderMessages && (
                          <div className="flex items-center justify-center py-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500"></div>
                          </div>
                        )}
                        {/* Load more hint */}
                        {hasMoreMessages && !loadingOlderMessages && conversationMessages.length >= MESSAGES_PER_PAGE && (
                          <p className="text-center text-xs text-slate-400 py-1">
                            {language === 'sr' ? '↑ Skroluj za starije poruke' : '↑ Scroll for older messages'}
                          </p>
                        )}
                        {loadingThread ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                          </div>
                        ) : conversationMessages.length === 0 ? (
                          <p className="text-center text-slate-500 py-8">{language === 'sr' ? 'Nema poruka' : 'No messages'}</p>
                        ) : (
                          <>
                            {conversationMessages.map((msg: any) => {
                              // Use isMe from API if available, otherwise compare senderId
                              const isMe = msg.isMe !== undefined ? msg.isMe : (msg.senderId === user?.id || String(msg.senderId) === String(user?.id));
                              return (
                                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[75%] p-3 rounded-xl ${
                                    isMe 
                                      ? 'bg-amber-500 text-white rounded-br-none' 
                                      : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-none shadow-sm'
                                  }`}>
                                    <p className="text-sm">{msg.content}</p>
                                    <span className={`text-[10px] block mt-1 ${isMe ? 'text-amber-100' : 'text-slate-400'}`}>
                                      {formatDate(msg.createdAt)}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Scroll anchor */}
                            <div ref={messagesEndRef} />
                          </>
                        )}
                      </div>

                      {/* Input */}
                      <div className="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendInConversation()}
                            placeholder={language === 'sr' ? 'Unesite poruku...' : 'Type a message...'}
                            className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                          <button
                            onClick={handleSendInConversation}
                            disabled={!newMessage.trim()}
                            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <p>{language === 'sr' ? 'Izaberite konverzaciju' : 'Select a conversation'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Termini */}
            {activeTab === 'termini' && (
              <div className="space-y-4">
                {/* Appointment Notifications Section */}
                {appointmentNotifications.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                      {language === 'sr' ? 'Obaveštenja o terminima' : 'Appointment notifications'}
                    </h3>
                    <div className="space-y-2">
                      {appointmentNotifications.map(notif => (
                        <motion.div
                          key={`notif-${notif.id}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex items-start gap-4 p-4 rounded-lg transition-colors cursor-pointer ${
                            notif.isRead
                              ? 'bg-slate-50 dark:bg-slate-700/50'
                              : 'bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500'
                          }`}
                          onClick={() => {
                            if (!notif.isRead) handleMarkNotificationRead(notif.id);
                            if (notif.link) navigate(notif.link);
                          }}
                        >
                          {getNotificationIcon(notif.type)}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 dark:text-white">{notif.title}</h4>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{notif.message}</p>
                            <span className="text-xs text-slate-500 mt-2 block">{formatDate(notif.createdAt)}</span>
                          </div>
                          {!notif.isRead && (
                            <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
                
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  {language === 'sr' ? 'Zahtevi za moje oglase' : 'Requests for my listings'}
                </h3>
                {paginatedData.length === 0 && appointmentNotifications.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    {language === 'sr' ? 'Nema zahteva za vaše oglase' : 'No requests for your listings'}
                  </div>
                ) : paginatedData.length === 0 ? (
                  <div className="text-center py-4 text-slate-500 text-sm">
                    {language === 'sr' ? 'Nema novih zahteva' : 'No new requests'}
                  </div>
                ) : (
                  (paginatedData as Appointment[]).map(apt => (
                    <motion.div
                      key={apt.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-4 rounded-xl border ${
                        apt.status === 'pending' 
                          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                          : apt.status === 'confirmed'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                          : 'bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          apt.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                          apt.status === 'confirmed' ? 'bg-emerald-200 text-emerald-800' :
                          apt.status === 'rejected' ? 'bg-red-200 text-red-800' :
                          'bg-slate-200 text-slate-800'
                        }`}>
                          {apt.status === 'pending' ? (language === 'sr' ? 'Na čekanju' : 'Pending') :
                           apt.status === 'confirmed' ? (language === 'sr' ? 'Potvrđeno' : 'Confirmed') :
                           apt.status === 'rejected' ? (language === 'sr' ? 'Odbijeno' : 'Rejected') :
                           (language === 'sr' ? 'Otkazano' : 'Cancelled')}
                        </span>
                        <span className="text-xs text-slate-500">{formatDate(apt.createdAt)}</span>
                      </div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-1">{apt.propertyTitle || `Oglas #${apt.propertyId}`}</h4>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        {language === 'sr' ? 'Zahtev od:' : 'Request from:'} <strong>{apt.requesterName || 'Korisnik'}</strong>
                      </p>
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-slate-700 dark:text-slate-300">{formatDateTime(apt.scheduledDate, apt.scheduledTime)}</span>
                      </div>
                      {apt.message && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 italic mt-2">"{apt.message}"</p>
                      )}
                      {apt.status === 'pending' ? (
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => handleAppointmentResponse(apt.id, 'confirmed')}
                            className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
                          >
                            {language === 'sr' ? 'Potvrdi' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => handleAppointmentResponse(apt.id, 'rejected')}
                            className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors"
                          >
                            {language === 'sr' ? 'Odbij' : 'Reject'}
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end mt-4">
                          <button
                            onClick={() => handleDeleteAppointment(apt.id)}
                            className="px-3 py-1 text-sm text-red-600 hover:text-red-500 flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {language === 'sr' ? 'Obriši' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Licitacije */}
            {activeTab === 'licitacije' && (
              <div className="space-y-4">
                {/* Sub-tabs for my bids vs received bids */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setBidsSubTab('my')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      bidsSubTab === 'my'
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {language === 'sr' ? 'Moje ponude' : 'My Bids'} ({myBids.length})
                  </button>
                  <button
                    onClick={() => setBidsSubTab('received')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      bidsSubTab === 'received'
                        ? 'bg-amber-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {language === 'sr' ? 'Primljene ponude' : 'Received Bids'} ({receivedBids.filter(b => b.status === 'pending').length})
                  </button>
                </div>

                {/* My Bids List */}
                {bidsSubTab === 'my' && (
                  myBids.length === 0 ? (
                    <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Nemate aktivnih ponuda na aukcijama' : 'You have no active bids on auctions'}
                    </div>
                  ) : (
                    myBids.map(bid => (
                      <motion.div
                        key={bid.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border border-slate-200 dark:border-slate-700"
                      >
                        <div className="flex gap-4">
                          {bid.propertyImage && (
                            <img src={bid.propertyImage} alt="" className="w-20 h-20 rounded-lg object-cover" />
                          )}
                          <div className="flex-1">
                            <Link to={`/oglas/${bid.propertyId}`} className="font-medium text-slate-900 dark:text-white hover:text-amber-600">
                              {bid.propertyTitle}
                            </Link>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {language === 'sr' ? 'Vaša ponuda:' : 'Your bid:'} <span className="font-semibold text-amber-600">{bid.amount.toLocaleString()} €</span>
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {language === 'sr' ? 'Trenutna cena:' : 'Current price:'} {bid.currentBid.toLocaleString()} €
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                bid.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                bid.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              }`}>
                                {bid.status === 'approved' ? (language === 'sr' ? 'Prihvaćeno' : 'Approved') :
                                 bid.status === 'rejected' ? (language === 'sr' ? 'Odbijeno' : 'Rejected') :
                                 (language === 'sr' ? 'Na čekanju' : 'Pending')}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {new Date(bid.createdAt).toLocaleDateString('sr-RS')}
                              </span>
                            </div>
                            {bid.ownerResponse && (
                              <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-700 rounded text-sm">
                                <span className="font-medium">{language === 'sr' ? 'Odgovor vlasnika:' : 'Owner response:'}</span> {bid.ownerResponse}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )
                )}

                {/* Received Bids List */}
                {bidsSubTab === 'received' && (
                  receivedBids.length === 0 ? (
                    <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Nemate primljenih ponuda' : 'You have no received bids'}
                    </div>
                  ) : (
                    receivedBids.map(bid => (
                      <motion.div
                        key={bid.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm border ${
                          bid.status === 'pending' ? 'border-amber-300 dark:border-amber-700' : 'border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex gap-4">
                          {bid.propertyImage && (
                            <img src={bid.propertyImage} alt="" className="w-20 h-20 rounded-lg object-cover" />
                          )}
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <Link to={`/oglas/${bid.propertyId}`} className="font-medium text-slate-900 dark:text-white hover:text-amber-600">
                                  {bid.propertyTitle}
                                </Link>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                  {language === 'sr' ? 'Ponuđač:' : 'Bidder:'} <strong>{bid.bidderName}</strong>
                                </p>
                              </div>
                              <span className="text-lg font-bold text-amber-600">{bid.amount.toLocaleString()} €</span>
                            </div>
                            {bid.comment && (
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 italic">"{bid.comment}"</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                bid.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                bid.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              }`}>
                                {bid.status === 'approved' ? (language === 'sr' ? 'Prihvaćeno' : 'Approved') :
                                 bid.status === 'rejected' ? (language === 'sr' ? 'Odbijeno' : 'Rejected') :
                                 (language === 'sr' ? 'Na čekanju' : 'Pending')}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {new Date(bid.createdAt).toLocaleDateString('sr-RS')}
                              </span>
                            </div>
                            {bid.status === 'pending' && (
                              <div className="flex gap-2 mt-4">
                                <button
                                  onClick={() => { setRespondingToBid(bid); setBidResponse(''); }}
                                  className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors"
                                >
                                  {language === 'sr' ? 'Prihvati' : 'Accept'}
                                </button>
                                <button
                                  onClick={() => { setRespondingToBid({ ...bid, status: 'rejected' as const }); setBidResponse(''); }}
                                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors"
                                >
                                  {language === 'sr' ? 'Odbij' : 'Reject'}
                                </button>
                              </div>
                            )}
                            {bid.ownerResponse && (
                              <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-700 rounded text-sm">
                                <span className="font-medium">{language === 'sr' ? 'Vaš odgovor:' : 'Your response:'}</span> {bid.ownerResponse}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )
                )}
              </div>
            )}
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      </div>

      {/* Reply Modal */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setReplyTo(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-lg w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {language === 'sr' ? 'Odgovor na poruku' : 'Reply to message'}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {language === 'sr' ? 'Za:' : 'To:'} {replyTo.senderName || 'Korisnik'}
                  </span>
                  {replyTo.isAdminMessage && (
                    <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">
                      Admin
                    </span>
                  )}
                </div>
                {/* Show original message */}
                <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{language === 'sr' ? 'Originalna poruka:' : 'Original message:'}</p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{replyTo.content}</p>
                </div>
              </div>
              <div className="p-6">
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  placeholder={language === 'sr' ? 'Vaša poruka...' : 'Your message...'}
                />
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setReplyTo(null)}
                    className="flex-1 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    {language === 'sr' ? 'Otkaži' : 'Cancel'}
                  </button>
                  <button
                    onClick={handleSendReply}
                    disabled={!replyContent.trim()}
                    className="flex-1 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50"
                  >
                    {language === 'sr' ? 'Pošalji' : 'Send'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bid Response Modal */}
      <AnimatePresence>
        {respondingToBid && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setRespondingToBid(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl max-w-lg w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                {respondingToBid.status === 'rejected' 
                  ? (language === 'sr' ? 'Odbij ponudu' : 'Reject Bid')
                  : (language === 'sr' ? 'Prihvati ponudu' : 'Accept Bid')}
              </h3>
              
              <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Ponuđač:' : 'Bidder:'} <strong>{respondingToBid.bidderName}</strong>
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Iznos:' : 'Amount:'} <strong className="text-amber-600">{respondingToBid.amount.toLocaleString()} €</strong>
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Za oglas:' : 'For listing:'} {respondingToBid.propertyTitle}
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'sr' ? 'Poruka za ponuđača (opciono):' : 'Message for bidder (optional):'}
                </label>
                <textarea
                  value={bidResponse}
                  onChange={(e) => setBidResponse(e.target.value)}
                  placeholder={language === 'sr' 
                    ? (respondingToBid.status === 'rejected' ? 'Razlog odbijanja...' : 'Dodatna poruka...')
                    : (respondingToBid.status === 'rejected' ? 'Reason for rejection...' : 'Additional message...')}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {language === 'sr' 
                    ? 'Ova poruka će biti poslata ponuđaču kao notifikacija i poruka.' 
                    : 'This message will be sent to the bidder as a notification and message.'}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setRespondingToBid(null)}
                  className="flex-1 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  {language === 'sr' ? 'Otkaži' : 'Cancel'}
                </button>
                <button
                  onClick={() => handleBidResponse(respondingToBid.status !== 'rejected')}
                  className={`flex-1 py-2 text-white font-medium rounded-lg ${
                    respondingToBid.status === 'rejected'
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-emerald-500 hover:bg-emerald-600'
                  }`}
                >
                  {respondingToBid.status === 'rejected' 
                    ? (language === 'sr' ? 'Odbij' : 'Reject')
                    : (language === 'sr' ? 'Prihvati' : 'Accept')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedChatId && (
        <ChatModal 
          chatId={selectedChatId} 
          onClose={() => {
            setSelectedChatId(null);
            setSearchParams(params => {
              params.delete('chat');
              return params;
            });
          }} 
        />
      )}
    </div>
  );
}

export default NotificationsPage;
